import {
  getCountriesReferencedInReport,
  getGeoCoordLibrary,
  getLatestValidDailyReportPerDay,
  rateOrUnRateReport,
  GeolegalReport as Report,
} from "@unruly-osdk/sdk";
import type { Client } from "@osdk/client";
import { Osdk } from "@osdk/client";
import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { format } from "date-fns";
import { foundryObjectType } from "../config/foundry";
import { ReportTypeEnum } from "../type/enum";

export interface RateReportInputs {
  report: Osdk.Instance<Report>;
  rating: number;
}

const keys = {
  all: ["reports"] as const,
  byId: (id: string | undefined) => [...keys.all, id] as const,
  dailies: () => [...keys.all, "dailies"] as const,
  weeklies: () => [...keys.all, "weeklies"] as const,
  latestDaily: (userId: string | undefined) =>
    [...keys.dailies(), userId] as const,
  daily: (date: Date, userId: string | undefined) =>
    [...keys.dailies(), format(date, "yyyy-MM-dd"), userId] as const,
  latestWeekly: (userId: string | undefined) =>
    [...keys.weeklies(), userId] as const,
  weekly: (date: Date, userId: string | undefined) =>
    [...keys.weeklies(), format(date, "yyyy-MM-dd"), userId] as const,
  reportEvents: (reportId: string) =>
    [...keys.all, "events", reportId] as const,
  geoCoordLibrary: () => [...keys.all, "geoCoordLibrary"] as const,
  latestValidDailyPerDay: (userId: string | undefined) =>
    [...keys.dailies(), "latestValidPerDay", userId] as const,
  allDailies: (userId: string | undefined) => [
    ...keys.dailies(),
    "all",
    userId,
  ],
};

function ReportService(queryClient: QueryClient, apiClient: Client) {
  const service = {
    useGetDailyReport: (
      date: Date | undefined,
      userId: string | undefined,
    ): UseQueryResult<Osdk.Instance<Report> | null, unknown> => {
      return useQuery({
        queryKey: date ? keys.daily(date, userId) : keys.latestDaily(userId),
        queryFn: async () =>
          date
            ? service.getDailyReport(date!, userId!)
            : service.getLatestDailyReport(userId!),
        enabled: !!userId,
      });
    },

    useGetWeeklyReport: (
      date: Date | undefined,
      userId: string | undefined,
    ): UseQueryResult<Osdk.Instance<Report> | null, unknown> => {
      return useQuery({
        queryKey: date ? keys.weekly(date, userId) : keys.latestWeekly(userId),
        queryFn: async () =>
          date
            ? service.getWeeklyReport(date!, userId!)
            : service.getLatestWeeklyReport(userId!),
        enabled: !!userId,
      });
    },

    getDailyReport: async (
      date: Date,
      userId: string,
    ): Promise<Osdk.Instance<Report>> => {
      return service.getReport(
        date,
        userId,
        ReportTypeEnum.NewDaily,
        ReportTypeEnum.Daily,
      );
    },

    getWeeklyReport: async (
      date: Date,
      userId: string,
    ): Promise<Osdk.Instance<Report>> => {
      return service.getReport(date, userId, ReportTypeEnum.Weekly);
    },

    getReport: async (
      date: Date,
      userId: string,
      ...reportTypes: ReportTypeEnum[]
    ): Promise<Osdk.Instance<Report>> => {
      try {
        const response = await apiClient(Report)
          .where({
            reportType: {
              $in: reportTypes,
            },
          })
          .where({
            reportDateRangeEnd: { $eq: format(date, "yyyy-MM-dd") },
          })
          .where({ owner: { $eq: userId } })
          .fetchPageWithErrors({
            $orderBy: { generatedAt: "desc" },
            $pageSize: 1,
          });

        if (response.value?.data?.[0]) {
          return response.value.data[0];
        } else {
          console.error("Error fetching report:", response.error);
          throw new Error("No report found for the specified date");
        }
      } catch (error) {
        console.error("Error fetching report:", error);
        throw error;
      }
    },

    getLatestDailyReport: async (
      userId: string,
    ): Promise<Osdk.Instance<Report> | null> => {
      return service.getLatestReport(
        userId,
        ReportTypeEnum.NewDaily,
        ReportTypeEnum.Daily,
      );
    },

    getLatestWeeklyReport: async (
      userId: string,
    ): Promise<Osdk.Instance<Report> | null> => {
      return service.getLatestReport(userId, ReportTypeEnum.Weekly);
    },

    getLatestReport: async (
      userId: string,
      ...reportTypes: ReportTypeEnum[]
    ): Promise<Osdk.Instance<Report> | null> => {
      const response = await apiClient(Report)
        .where({
          reportType: { $in: reportTypes },
        })
        .where({ owner: { $eq: userId } })
        .fetchPage({
          $orderBy: { generatedAt: "desc" },
          $pageSize: 1,
        });
      return response.data[0] || null;
    },

    useGetReportById: (
      id: string | undefined,
    ): UseQueryResult<Osdk.Instance<Report>, unknown> => {
      return useQuery({
        queryKey: keys.byId(id),
        queryFn: async () => apiClient(Report).fetchOne(id!),
        enabled: !!id,
      });
    },

    rateReport: async (report: Osdk.Instance<Report>, rating: number) => {
      try {
        return await apiClient(rateOrUnRateReport).applyAction(
          {
            report,
            rating,
          },
          {
            $returnEdits: false,
          },
        );
      } catch (error) {
        console.error("Error rating a report:", error);
        throw error;
      }
    },

    useAddReportReaction: (): UseMutationResult<
      unknown,
      unknown,
      RateReportInputs,
      unknown
    > => {
      return useMutation({
        mutationFn: async ({ report, rating }: RateReportInputs) =>
          service.rateReport(report, rating),
        onSuccess: async (_data, { report }) => {
          if (
            report.reportType === ReportTypeEnum.Daily ||
            report.reportType === ReportTypeEnum.NewDaily
          ) {
            await queryClient.invalidateQueries({ queryKey: keys.dailies() });
          }
        },
      });
    },

    getLatestValidDailyReportPerDay: async (
      userId: string,
    ): Promise<string[]> => {
      try {
        return await apiClient(getLatestValidDailyReportPerDay).executeFunction(
          {
            user: userId,
          },
        );
      } catch (error) {
        console.error("Error fetching plain text:", error);
        throw error;
      }
    },

    getReportEvents: async (reportId: string) => {
      try {
        // First, we need to get the report instance from the reportId
        const report = await apiClient(Report).fetchOne(reportId);

        // Then call the SDK function with the report instance and required parameters
        const result = await apiClient(
          getCountriesReferencedInReport,
        ).executeFunction({
          report,
          eventObjectTypeId: foundryObjectType("event"),
        });

        return result;
      } catch (error) {
        console.error("Error fetching report events:", error);
        throw error;
      }
    },

    useGetReportEvents: (reportId: string) => {
      return useQuery({
        queryKey: keys.reportEvents(reportId),
        queryFn: async () => service.getReportEvents(reportId),
        enabled: !!reportId,
      });
    },

    useGetLatestValidDailyReportPerDay: (
      userId: string | undefined,
    ): UseQueryResult<string[], unknown> => {
      return useQuery({
        queryKey: keys.latestValidDailyPerDay(userId),
        queryFn: async () => service.getLatestValidDailyReportPerDay(userId!),
        enabled: !!userId,
      });
    },

    getPDBReportsInfinite: async (
      userId: string,
      pageSize: number = 20,
      nextPageToken?: string,
    ): Promise<{
      data: string[];
      nextPageToken?: string;
    }> => {
      try {
        const response = await apiClient(Report)
          .where({
            reportType: {
              $in: [ReportTypeEnum.Daily, ReportTypeEnum.NewDaily],
            },
            owner: { $eq: userId },
          })
          .fetchPage({
            $select: ["id", "reportDateRangeEnd", "generatedAt"],
            $orderBy: { reportDateRangeEnd: "desc" },
            $pageSize: pageSize,
            ...(nextPageToken && { $nextPageToken: nextPageToken }),
          });

        // Group reports by day (YYYY-MM-DD) and get the latest one per day
        const reportsByDay = new Map<
          string,
          { reportDateRangeEnd: string; generatedAt?: string }
        >();

        for (const report of response.data || []) {
          const reportDate = report.reportDateRangeEnd;
          if (reportDate) {
            // Extract just the date part (YYYY-MM-DD) for grouping by day
            const dayKey = reportDate.substring(0, 10);

            // If we don't have a report for this day, or if this report is newer
            if (
              !reportsByDay.has(dayKey) ||
              (report.generatedAt &&
                reportsByDay.get(dayKey)?.generatedAt &&
                new Date(report.generatedAt) >
                  new Date(reportsByDay.get(dayKey)!.generatedAt!))
            ) {
              reportsByDay.set(dayKey, {
                reportDateRangeEnd: report.reportDateRangeEnd,
                generatedAt: report.generatedAt,
              });
            }
          }
        }

        // Convert to array of date strings
        const latestReportsPerDay = Array.from(reportsByDay.values()).map(
          (report) => report.reportDateRangeEnd,
        );

        return {
          data: latestReportsPerDay,
          nextPageToken: response.nextPageToken,
        };
      } catch (error) {
        console.error("Error fetching PDB reports infinite:", error);
        throw error;
      }
    },

    useGetPDBReportsInfinite: (userId: string, pageSize: number) => {
      return useInfiniteQuery({
        queryKey: keys.allDailies(userId),
        queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
          return service.getPDBReportsInfinite(userId, pageSize, pageParam);
        },
        getNextPageParam: (lastPage) => lastPage.nextPageToken,
        enabled: !!userId,
        initialPageParam: undefined as string | undefined,
      });
    },
    getGeoCoordLibrary: async () => {
      const result = await apiClient(getGeoCoordLibrary).executeFunction();
      return result.locations as Array<{
        name: string;
        lat: number;
        lng: number;
      }>;
    },

    useGetGeoCoordLibrary: () => {
      return useQuery({
        queryKey: keys.geoCoordLibrary(),
        queryFn: () => service.getGeoCoordLibrary(),
        staleTime: 24 * 60 * 60 * 1000, // 24 hours — this data rarely changes
      });
    },
  };
  return service;
}

export default ReportService;
